import React from 'react';

import PublicIcon from '@mui/icons-material/Public';
import { Alert, Box, Card, CardContent, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';

import { Bundle } from '~/types/bundles/bundle';

import { regionName } from './regions';
import useBundleRegions from './useBundleRegions';

interface Props {
  bundle: Bundle;
}

function RegionRow({ label, regions, isMain }: { label: string; regions: Array<string>; isMain?: boolean }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'center' }}>
      <Typography
        variant="body2"
        color={isMain ? 'text.primary' : 'text.secondary'}
        fontWeight={isMain ? 'medium' : undefined}
        sx={{ minWidth: 150 }}
      >
        {label}
      </Typography>
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {regions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          regions.map((region) => (
            <Chip
              key={region}
              size="small"
              label={regionName(region)}
              color={isMain ? 'primary' : 'default'}
              variant={isMain ? 'filled' : 'outlined'}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
}

/** Where the bundle can sell, worked out from its tour and hotel. */
export default function BundleRegions({ bundle }: Props) {
  const { hasTour, hasHotel, tourRegions, hotelRegions, shared, isLoading, error } = useBundleRegions(bundle);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <PublicIcon fontSize="small" color="action" />
          <Typography variant="h6">Regions</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A bundle sells where its tour is scheduled and its hotel is on sale.
        </Typography>

        {!hasTour || !hasHotel ? (
          <Typography color="text.secondary">Add the tour and the hotel to see where the bundle can sell.</Typography>
        ) : isLoading ? (
          <Stack spacing={1}>
            <Skeleton width="60%" />
            <Skeleton width="50%" />
            <Skeleton width="40%" />
          </Stack>
        ) : error ? (
          <Alert severity="warning">
            The tour or hotel couldn't be loaded, so where the bundle sells isn't known. Reload the page to try again.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <RegionRow label="Tour scheduled in" regions={tourRegions} />
            <RegionRow label="Hotel on sale in" regions={hotelRegions} />
            <Divider />
            <RegionRow label="Bundle sells in" regions={shared} isMain />
            {shared.length === 0 && (
              <Box>
                <Alert severity="error">
                  The tour isn't scheduled anywhere the hotel is on sale, so this bundle can't be sold. Choose a hotel
                  on sale in one of the tour's regions.
                </Alert>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
